import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../../utils/utils';
import { flexRender, getCoreRowModel, useReactTable, getSortedRowModel } from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../icons';
import SortIcon from '../../../icons/newIcons/SortIcon';
import { useEffect, useMemo, useRef, useState } from 'react';
export { createColumnHelper } from '@tanstack/react-table';
// Stable, so TanStack doesn't recompute the sorted rows on every render.
const NO_SORTING = [];
const ARIA_SORT = {
    asc: 'ascending',
    desc: 'descending',
    none: 'none'
};
const getPinnedStyles = (column, zIndex) => {
    const pinned = column.getIsPinned();
    if (!pinned)
        return undefined;
    const hasExplicitSize = column.columnDef.size !== undefined;
    return {
        position: 'sticky',
        left: pinned === 'left' ? column.getStart('left') : undefined,
        right: pinned === 'right' ? column.getAfter('right') : undefined,
        width: hasExplicitSize ? column.getSize() : undefined,
        zIndex
    };
};
export const Table = ({ data, columns, rowProps, pagination, loading = false, pinFirstColumn = false, sorting, onSortingChange, manualSorting = false, getRowId, selectedRowId, onRowClick }) => {
    const firstColumnId = useMemo(() => {
        if (!pinFirstColumn)
            return undefined;
        const first = columns[0];
        return first?.id ?? first?.accessorKey;
    }, [pinFirstColumn, columns]);
    // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table returns non-memoizable functions by design
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId,
        enableSorting: onSortingChange !== undefined,
        enableMultiSort: false,
        enableSortingRemoval: false,
        manualSorting,
        sortDescFirst: true,
        state: { sorting: sorting ?? NO_SORTING },
        onSortingChange: (updater) => onSortingChange?.(typeof updater === 'function'
            ? updater(sorting ?? NO_SORTING)
            : updater),
        initialState: firstColumnId
            ? { columnPinning: { left: [firstColumnId], right: [] } }
            : undefined
    });
    const [width, setWidth] = useState({ scroll: 0, wrapper: 0 });
    const [isScrolled, setIsScrolled] = useState(false);
    const tableWrapperRef = useRef(null);
    const tableRef = useRef(null);
    const scrollWrapperRef = useRef(null);
    useEffect(() => {
        const table = tableRef.current;
        const tableWrapper = tableWrapperRef.current;
        const resizeObserver = new ResizeObserver(() => {
            if (table && tableWrapper) {
                const scrollWidth = table.offsetWidth;
                const wrapperWidth = tableWrapper.offsetWidth;
                setWidth({
                    scroll: scrollWidth,
                    wrapper: wrapperWidth
                });
            }
        });
        if (table) {
            resizeObserver.observe(table);
        }
        return () => {
            if (table) {
                resizeObserver.unobserve(table);
            }
        };
    }, []);
    return (_jsxs("div", { className: "relative w-full max-w-full isolate", children: [_jsx("div", { className: "max-w-full overflow-x-auto overscroll-x-none [&::-webkit-scrollbar]:hidden", ref: tableWrapperRef, onScroll: () => {
                    const left = tableWrapperRef.current?.scrollLeft ?? 0;
                    if (scrollWrapperRef.current) {
                        scrollWrapperRef.current.scrollLeft = left;
                    }
                    setIsScrolled(left > 0);
                }, children: _jsxs("table", { className: "w-full", ref: tableRef, children: [_jsx("thead", { className: "border-y border-solid border-neutral-200 bg-neutral-100", children: table.getHeaderGroups().map((headerGroup) => (_jsx("tr", { children: headerGroup.headers.map((header) => {
                                    const isPinned = header.column.getIsPinned() === 'left';
                                    const meta = header.column.columnDef
                                        .meta;
                                    const canSort = !header.isPlaceholder &&
                                        header.column.getCanSort();
                                    const sorted = header.column.getIsSorted() || 'none';
                                    const label = header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext());
                                    return (_jsx("th", { "aria-sort": canSort
                                            ? ARIA_SORT[sorted]
                                            : undefined, style: getPinnedStyles(header.column, 20), className: cn('px-3 py-2.5 relative text-left text-[0.8125rem] font-normal leading-5 text-neutral-600', isPinned && 'bg-neutral-100', isPinned &&
                                            isScrolled &&
                                            'after:absolute after:inset-0 after:pointer-events-none after:shadow-[inset_-1px_0_0_0_rgb(229_229_229),3px_0_4px_-2px_rgba(0,0,0,0.08)]', meta?.headClassName), children: _jsxs("span", { className: "inline-flex min-w-max select-none items-center font-sans gap-1", children: [canSort ? (_jsxs("button", { type: "button", className: "inline-flex cursor-pointer items-center gap-1 rounded-sm outline-none hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-primary-base/40", onClick: header.column.getToggleSortingHandler(), children: [label, _jsx(SortIcon, { direction: sorted, className: cn('size-5 shrink-0', sorted ===
                                                                'none'
                                                                ? 'text-neutral-400'
                                                                : 'text-neutral-700') })] })) : (label), meta?.headInfo] }) }, header.id));
                                }) }, headerGroup.id))) }), _jsx("tbody", { className: "divide-y divide-solid divide-neutral-200", children: loading
                                ? Array.from({ length: 15 }).map((_, index) => (_jsx("tr", { className: "group bg-white", children: table
                                        .getHeaderGroups()
                                        .map((headerGroup) => headerGroup.headers.map((header) => {
                                        const isPinned = header.column.getIsPinned() ===
                                            'left';
                                        return (_jsx("td", { style: getPinnedStyles(header.column, 10), className: cn('px-3 py-2 text-[0.8125rem] text-neutral-900', isPinned &&
                                                'bg-inherit', isPinned &&
                                                isScrolled &&
                                                'after:absolute after:inset-0 after:pointer-events-none after:shadow-[inset_-1px_0_0_0_rgb(229_229_229),3px_0_4px_-2px_rgba(0,0,0,0.08)]'), children: _jsx("span", { className: "bg-neutral-200 inline-block animate-pulse rounded-full h-3.5 w-full align-middle" }) }, header.id));
                                    })) }, index)))
                                : table.getRowModel().rows.map((row) => (_jsx("tr", { tabIndex: onRowClick ? 0 : undefined, "aria-selected": row.id === selectedRowId || undefined, className: cn('group bg-white hover:bg-neutral-100', (rowProps?.onClick != null ||
                                        onRowClick != null) &&
                                        'hover:cursor-pointer', onRowClick != null &&
                                        'outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-base/40', row.id === selectedRowId &&
                                        'bg-primary-subtle hover:bg-primary-subtle'), onClick: () => {
                                        rowProps?.onClick?.(row.original);
                                        onRowClick?.(row.original);
                                    }, onKeyDown: onRowClick
                                        ? (event) => activateRow(event, () => onRowClick(row.original))
                                        : undefined, children: row.getVisibleCells().map((cell) => {
                                        const isPinned = cell.column.getIsPinned() ===
                                            'left';
                                        return (_jsx("td", { style: getPinnedStyles(cell.column, 10), className: cn('px-3 py-2 text-[0.8125rem] text-neutral-900', isPinned && 'bg-inherit', isPinned &&
                                                isScrolled &&
                                                'after:absolute after:inset-0 after:pointer-events-none after:shadow-[inset_-1px_0_0_0_rgb(229_229_229),3px_0_4px_-2px_rgba(0,0,0,0.08)]', cell.column.columnDef
                                                .meta?.cellClassName), children: flexRender(cell.column.columnDef
                                                .cell, cell.getContext()) }, cell.id));
                                    }) }, row.id))) })] }) }), _jsx("div", { className: cn('sticky bottom-0 z-30 h-3 max-w-full translate-y-0.5 overflow-x-scroll overscroll-x-none border-t border-solid border-neutral-200 bg-white [scrollbar-width:thin] [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar]:bg-neutral-100 [&::-webkit-scrollbar-track]:bg-neutral-100 [&::-webkit-scrollbar-thumb]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-neutral-500', {
                    'bottom-12': pagination
                }), ref: scrollWrapperRef, onScroll: () => {
                    if (tableWrapperRef.current) {
                        tableWrapperRef.current.scrollLeft =
                            scrollWrapperRef.current?.scrollLeft ?? 0;
                    }
                }, style: {
                    display: width.scroll > width.wrapper ? 'block' : 'none'
                }, children: _jsx("div", { style: {
                        width: width.scroll
                    } }) }), pagination && _jsx(Pagination, { ...pagination })] }));
};
// Enter or Space on the row itself, not on a control inside one of its cells.
const activateRow = (event, activate) => {
    if (event.target !== event.currentTarget)
        return;
    if (event.key !== 'Enter' && event.key !== ' ')
        return;
    event.preventDefault();
    activate();
};
export const Pagination = ({ onPageChange, pageIndex, pageCount, previousLabel = 'Previous', nextLabel = 'Next' }) => {
    return (_jsxs("div", { className: "sticky bottom-0 z-30 flex justify-end gap-1 border-t border-solid border-neutral-200 bg-neutral-100 px-3 py-2", children: [_jsx("button", { type: "button", className: "group rounded-md bg-neutral-200 p-2 h-[1.875rem] w-[1.875rem] hover:bg-neutral-300/80 transition-colors disabled:border-transparent disabled:bg-neutral-300/30 cursor-pointer disabled:cursor-default border border-solid border-neutral-300", "aria-label": previousLabel, disabled: pageIndex === 0, onClick: () => onPageChange(pageIndex - 1), children: _jsx(ChevronLeftIcon, { className: "size-3 mr-px [&>path]:fill-neutral-700 group-disabled:[&>path]:fill-neutral-400/70" }) }), _jsx("button", { type: "button", className: "group rounded-md bg-neutral-200 p-2 h-[1.875rem] w-[1.875rem] disabled:border-transparent transition-colors hover:bg-neutral-300/80 disabled:bg-neutral-300/30 cursor-pointer disabled:cursor-default border border-solid border-neutral-300", "aria-label": nextLabel, disabled: pageCount < 1 ? true : pageIndex + 1 === pageCount, onClick: () => onPageChange(pageIndex + 1), children: _jsx(ChevronRightIcon, { className: "size-3 ml-px [&>path]:fill-neutral-700 group-disabled:[&>path]:fill-neutral-400/70" }) })] }));
};
